import type {
  AgentRegistryProjectModel,
  AgentRegistryRequestModel,
  ExternalIntegrationModel,
  TlxHttpResponse,
} from "@/api";
import { agentEstateStore } from "@/api/mock-data/agent-estate/store";
import type { AgentEnvironment } from "@/api/temp/AgentEstateService";
import {
  AZURE_AGENT_REGISTRY,
  REGISTRY_IDENTITY,
  REGISTRY_PLATFORM,
} from "@/pages/AgentEstate/registry/agentRegistryConfiguration";
import type {
  RegistryCheck,
  RegistryImportResult,
  RegistryOnboardingResult,
  RegistryProject,
  RegistryProjectPreview,
  RegistryValidationResult,
} from "@/pages/AgentEstate/registry/types";

/**
 * Demo stand-in for the agent-registry API, served inside `<MockScope>`
 * (`isAgentsMock`, see `src/configs/mock.ts`). Everywhere else these calls
 * reach the backend.
 *
 * A demo connection made through the preview onboarding wizard lands in the
 * agent-estate store as an environment, not here - so `list()` also reports
 * every such environment as a registry. That keeps
 * the Agent Registries page, the agent list's registry column and the import
 * badges in agreement with the demo agents.
 *
 * Mirrors the TRUST-13366 contract closely enough to click through:
 * - a secret containing "fail" fails `PROVIDER_AUTHENTICATION`, and the
 *   checks after it are `NOT_CHECKED`;
 * - a client ID containing "noidentity" fails `AGENT_IDENTITY_READ`;
 * - a connection name containing "empty" previews no projects.
 */

const PROJECTS: RegistryProject[] = [
  {
    id: "tlx-gtm-foundry-resource/tlx-gtm-foundry",
    accountName: "tlx-gtm-foundry-resource",
    projectName: "tlx-gtm-foundry",
  },
  {
    id: "tlxmsdemo-resource/tlxmsdemo",
    accountName: "tlxmsdemo-resource",
    projectName: "tlxmsdemo",
  },
];

const DEMO_GATEWAY_URL = "https://gateway.trustlogix.io/mcp";

const AGENTS = ["Support-TLX", "Analytics-TLX", "Claims-Triage"];

const envelope = <T>(code: number, message: string, data: T) => ({
  code,
  message,
  data: data as unknown as object,
});

/** Rejects the way axios would, so callers read `response.data` unchanged. */
const reject = (status: number, message: string, data?: unknown): never => {
  throw {
    response: { status, data: { code: status, message, data } },
  };
};

const check = (
  code: string,
  passed: boolean,
  failure?: Partial<RegistryCheck>,
): RegistryCheck =>
  passed ? { code, status: "PASSED" } : { code, status: "FAILED", ...failure };

const notChecked = (code: string): RegistryCheck => ({
  code,
  status: "NOT_CHECKED",
});

/** A saved registry as the list returns it: secret masked, gateway attested. */
const savedRegistry = ({
  id,
  integrationType = AZURE_AGENT_REGISTRY,
  name,
  tenantId,
  clientId,
  selectedProjects,
  confirmedAt,
  agentCount,
}: {
  id: string;
  integrationType?: string;
  name?: string;
  tenantId?: string;
  clientId?: string;
  selectedProjects: AgentRegistryProjectModel[];
  confirmedAt: string;
  agentCount?: number;
}): ExternalIntegrationModel => ({
  id,
  type: "AGENT_REGISTRY",
  integrationType,
  status: "ACTIVE",
  agentCount,
  integrationDetails: {
    name,
    tenantId,
    clientId,
    clientSecret: "*****",
    selectedProjects,
    identity: { ...REGISTRY_IDENTITY },
    prerequisites: {
      // Resolved by the server from the tenant's gateway; a client value is
      // ignored, as the real API does.
      gatewayId: DEMO_GATEWAY_URL,
      requiredMcpServicesConfirmed: true,
      confirmedBy: "demo-user",
      confirmedAt,
    },
  },
});

/** Microsoft Foundry environments are Azure registries; others keep their platform. */
const environmentAsRegistry = (
  environment: AgentEnvironment,
): ExternalIntegrationModel =>
  savedRegistry({
    id: environment.id,
    integrationType:
      environment.platform === REGISTRY_PLATFORM
        ? AZURE_AGENT_REGISTRY
        : environment.platform,
    name: environment.name,
    tenantId: "64ddf09f-4f98-4b98-a806-f947d4d7a672",
    clientId: "6821e6ac-c1ef-4a04-8765-a826df0fccc9",
    selectedProjects: (environment.projects ?? []).map(project => ({
      id: project.id,
      accountName: project.id.split("/")[0],
      projectName: project.name,
    })),
    confirmedAt: environment.startedAt,
    agentCount: environment.totalAgents,
  });

class AgentRegistryMockStore {
  private registries: ExternalIntegrationModel[] = [];

  private find(id: string): ExternalIntegrationModel | undefined {
    return this.list().find(item => item.id === id);
  }

  validate({ body }: { body: AgentRegistryRequestModel }): TlxHttpResponse {
    const connection = body.configuration?.connection ?? {};
    const target = body.target ?? "CONNECTION_AND_IDENTITY";
    const authenticated = !(connection.clientSecret ?? "").includes("fail");
    const identityReadable = !(connection.clientId ?? "").includes(
      "noidentity",
    );
    const wantsConnection = target !== "IDENTITY";
    const wantsIdentity = target !== "CONNECTION";

    const connectionChecks = wantsConnection
      ? authenticated
        ? [
            check("PROVIDER_AUTHENTICATION", true),
            check("PROJECT_DISCOVERY_ACCESS", true),
          ]
        : [
            check("PROVIDER_AUTHENTICATION", false, {
              message:
                "Microsoft rejected the tenant ID, client ID or client secret.",
            }),
            notChecked("PROJECT_DISCOVERY_ACCESS"),
          ]
      : [
          notChecked("PROVIDER_AUTHENTICATION"),
          notChecked("PROJECT_DISCOVERY_ACCESS"),
        ];
    const identityCheck = !wantsIdentity || !authenticated
      ? notChecked("AGENT_IDENTITY_READ")
      : check("AGENT_IDENTITY_READ", identityReadable, {
          message:
            "The Microsoft Graph permission AgentIdentity.Read.All, with admin consent, is missing.",
          missingPermissions: ["AgentIdentity.Read.All"],
        });
    const statusOf = (checks: RegistryCheck[]) =>
      checks.some(item => item.status === "FAILED")
        ? "FAILED"
        : checks.every(item => item.status === "PASSED")
          ? "PASSED"
          : "NOT_CHECKED";

    const result: RegistryValidationResult = {
      results: {
        connection: {
          status: statusOf(connectionChecks),
          checks: connectionChecks,
        },
        identity: {
          provider: "ENTRA_ID",
          credentialSource: "REGISTRY_CONNECTION",
          status: statusOf([identityCheck]),
          checks: [identityCheck],
        },
      },
      checkedAt: new Date().toISOString(),
    };
    return envelope(200, "Validation completed", result);
  }

  private projectsFor(body: AgentRegistryRequestModel): RegistryProject[] {
    return (body.configuration?.name ?? "").includes("empty") ? [] : PROJECTS;
  }

  preview({ body }: { body: AgentRegistryRequestModel }): TlxHttpResponse {
    const items = this.projectsFor(body);
    const preview: RegistryProjectPreview = {
      scope: "ACCESSIBLE_PROJECTS",
      items,
      warnings: [
        {
          code: "ACCESSIBLE_PROJECTS_ONLY",
          message: "Only projects this connection can read are included.",
        },
      ],
      complete: true,
      total: items.length,
    };
    return envelope(200, "Projects listed", preview);
  }

  private importFor(registry: ExternalIntegrationModel): RegistryImportResult {
    const projects = registry.integrationDetails?.selectedProjects ?? [];
    return {
      created: projects.flatMap(project =>
        AGENTS.map(name => ({
          applicationId: `${registry.id}-${name}`,
          name,
          externalAgentId: name,
          projectId: project.id,
        })),
      ),
      updated: [],
      deleted: [],
      skipped: [
        {
          externalAgentId: "Legacy",
          name: "Legacy",
          projectId: projects[0]?.id,
          reason:
            "TrustLogix cannot identify this agent because it has no Microsoft Entra agent identity.",
        },
      ],
    };
  }

  onboard({ body }: { body: AgentRegistryRequestModel }): TlxHttpResponse {
    const { configuration, prerequisites, projectSelection } = body;
    if (!prerequisites?.requiredMcpServicesConfirmed)
      reject(400, "Confirm the MCP services.", "MCP_CONFIRMATION_REQUIRED");
    const listed = this.projectsFor(body).map(project => project.id);
    const sent = projectSelection?.projectIds ?? [];
    if (sent.length === 0)
      reject(400, "Select at least one project.", "EMPTY_PROJECT_SELECTION");
    // `ALL_LISTED` must name every listed project; a `SELECTED` subset (the
    // wizard's narrowed selection) only listed ones.
    const isAllListed = projectSelection?.mode !== "SELECTED";
    if (
      sent.some(id => !listed.includes(id)) ||
      (isAllListed && sent.length !== listed.length)
    )
      reject(409, "The projects changed.", "PROJECT_SELECTION_CHANGED");

    const existing = configuration.id ? this.find(configuration.id) : undefined;
    const id = existing?.id ?? `mock-registry-${Date.now()}`;
    const registry = savedRegistry({
      id,
      name: configuration.name,
      tenantId: configuration.connection?.tenantId,
      clientId: configuration.connection?.clientId,
      selectedProjects: PROJECTS.filter(project => sent.includes(project.id)),
      confirmedAt: new Date().toISOString(),
    });
    this.registries = [
      registry,
      ...this.registries.filter(item => item.id !== id),
    ];
    const result: RegistryOnboardingResult = {
      registryId: id,
      registry,
      import: this.importFor(registry),
    };
    return envelope(
      existing ? 200 : 201,
      "Agent registry onboarded",
      result,
    );
  }

  importAgents({ id }: { id: string }): TlxHttpResponse {
    const registry = this.find(id);
    if (!registry)
      reject(404, "The agent registry could not be found.", "NOT_FOUND");
    return envelope(200, "Agents imported", this.importFor(registry!));
  }

  /** Demo attribute sync: one created attribute, the registry's demo agents updated. */
  syncAttributes({ id }: { id: string }): TlxHttpResponse {
    const registry = this.find(id);
    if (!registry)
      reject(404, "The agent registry could not be found.", "NOT_FOUND");
    return envelope(200, "Agent attributes synced", {
      sourceConfigurationId: id,
      attributesCreated: ["Department"],
      attributesSkipped: [],
      agentsUpdated: (this.importFor(registry!).created ?? []).map(
        agent => agent.applicationId,
      ),
      agentsSkipped: [],
    });
  }

  remove({ id }: { id: string }): TlxHttpResponse {
    if (!this.find(id))
      reject(404, "The agent registry could not be found.", "NOT_FOUND");
    this.registries = this.registries.filter(item => item.id !== id);
    // Deleting a demo environment's registry disconnects the environment.
    if (agentEstateStore.listEnvironments().some(item => item.id === id))
      agentEstateStore.disconnect(id);
    return envelope(200, "Agent registry deleted", null);
  }

  /** Saved registries first, so an edited environment shows its new values. */
  list(): ExternalIntegrationModel[] {
    const saved = new Set(this.registries.map(item => item.id));
    return [
      ...this.registries,
      ...agentEstateStore
        .listEnvironments()
        .filter(environment => !saved.has(environment.id))
        .map(environmentAsRegistry),
    ];
  }

  reset() {
    this.registries = [];
  }
}

export const agentRegistryStore = new AgentRegistryMockStore();
